<template>
  <v-form
    ref="vehicleFormRef"
    class="form form--with-sticky-actions page-stack grid-stack grid-gap-page-section"
    @submit.prevent="submitVehicleFormAsync"
  >
    <section class="page-panel">
      <header class="page-panel__header">
        <h3 class="lead-text ma-0">Osnovni podaci</h3>
      </header>

      <div class="page-panel__content grid-stack grid-row-gap-18">
        <AppSelect
          v-model="vehicleFormValues.brandCode"
          :items="brandOptions"
          label="Marka"
          width="560px"
          required
        />

        <AppTextField
          v-model="vehicleFormValues.name"
          label="Tip vozila"
          placeholder="npr. Golf 8 GTI"
          :rules="nameRules"
          width="560px"
          required
        />

        <AppTextField
          v-model="vehicleFormValues.plate"
          label="Registracijska oznaka"
          :rules="plateRules"
          width="560px"
          required
        />

        <AppTextField
          v-model="vehicleFormValues.vin"
          label="Broj šasije"
          :rules="vinRules"
          width="560px"
          required
        />

        <AppTextField
          v-model="vehicleFormValues.color"
          label="Boja"
          :rules="colorRules"
          width="560px"
          required
        />

        <div class="vehicle-form__usage-field">
          <p class="control-label">
            Vrsta korištenja <span class="control__required">*</span>
          </p>

          <v-radio-group
            class="vehicle-form__usage-options"
            hide-details="auto"
            inline
            :model-value="vehicleFormValues.usageArea"
            :rules="requiredRules"
            @update:model-value="setUsageArea"
          >
            <v-radio
              class="checkbox"
              label="Vozni park"
              value="drivepark"
            />
            <v-radio
              class="checkbox"
              label="Događaji"
              value="events"
            />
          </v-radio-group>
        </div>

        <AppSelect
          v-if="vehicleFormValues.usageArea === 'drivepark'"
          v-model="vehicleFormValues.purpose"
          :items="vehiclePurposeOptions"
          label="Namjena"
          width="560px"
          required
        />

        <template v-if="isEditMode">
          <AppSelect
            :items="statusOptions"
            label="Status"
            :model-value="vehicleFormValues.isActive"
            width="560px"
            required
            @update:model-value="setVehicleStatus"
          />

          <p
            v-if="!vehicleFormValues.isActive"
            class="notice"
          >
            Vozilo neće biti dostupno za rezervacije dok se status ručno ne promijeni nazad u „Aktivno”.
          </p>
        </template>
      </div>
    </section>

    <VehicleUsageHistory
      v-if="isEditMode"
      :vehicle-id="initialVehicleValues.id"
    />

    <div class="submit-bar">
      <ToolbarActionButton
        :text="submitText"
        type="submit"
        icon=""
        :disabled="isSavingVehicle"
        :loading="isSavingVehicle"
      />
    </div>

    <AppConfirmDialog
      v-model="isStatusWarningOpen"
      variant="blocked"
      title="Vozilo nije moguće označiti kao neispravno."
      :subtitle="`${vehicleFormValues.name} · ${vehicleFormValues.plate}`"
      message="Ovo vozilo ima aktivne rezervacije u odabranom razdoblju neispravnosti. Uklonite ili premjestite rezervacije i zatim pokušajte ponovno označiti vozilo kao neispravno."
      primary-text="Razumijem"
    />
  </v-form>
</template>

<script setup>
import { reactive, ref } from 'vue'

import AppConfirmDialog from './AppConfirmDialog.vue'
import AppSelect from './AppSelect.vue'
import AppTextField from './AppTextField.vue'
import ToolbarActionButton from './ToolbarActionButton.vue'
import VehicleUsageHistory from './VehicleUsageHistory.vue'
import { canAccessBrand } from '../auth/authorization'
import { getVehicleBrandOptions, vehiclePurposeOptions } from '../helpers'

const props = defineProps({
  initialVehicleValues: {
    type: Object,
    default: null,
  },

  submitText: {
    type: String,
    required: true,
  },

  isSavingVehicle: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['submit'])

const isEditMode = props.initialVehicleValues !== null
const brandOptions = getVehicleBrandOptions().filter(({ value }) => canAccessBrand(value))
const statusOptions = [
  { title: 'Aktivno', value: true },
  { title: 'Neispravno', value: false },
]
const requiredRules = [
  (value) => Boolean(value) || 'Obavezno polje',
]
const nameRules = [maxLengthRule(255)]
const plateRules = [maxLengthRule(20)]
const vinRules = [maxLengthRule(17)]
const colorRules = [maxLengthRule(50)]
const vehicleFormRef = ref(null)
const isStatusWarningOpen = defineModel('isStatusWarningOpen', {
  type: Boolean,
  default: false,
})
const vehicleFormValues = reactive({
  brandCode: props.initialVehicleValues?.brandCode ?? null,
  name: props.initialVehicleValues?.name ?? '',
  plate: props.initialVehicleValues?.plate ?? '',
  vin: props.initialVehicleValues?.vin ?? '',
  color: props.initialVehicleValues?.color ?? '',
  usageArea: props.initialVehicleValues?.isForDrive === false
    ? 'events'
    : 'drivepark',
  purpose: props.initialVehicleValues?.purpose ?? null,
  isActive: props.initialVehicleValues?.isActive ?? true,
})

function maxLengthRule (maximumLength) {
  return (value) => String(value ?? '').trim().length <= maximumLength ||
    `Najviše ${maximumLength} znakova`
}

function setUsageArea (usageArea) {
  vehicleFormValues.usageArea = usageArea

  if (usageArea === 'events') {
    vehicleFormValues.purpose = null
  }
}

function setVehicleStatus (isActive) {
  if (!isActive && props.initialVehicleValues?.isOccupied) {
    isStatusWarningOpen.value = true
    return
  }

  vehicleFormValues.isActive = isActive
}

async function submitVehicleFormAsync () {
  if (props.isSavingVehicle) {
    return
  }

  const validationResult = await vehicleFormRef.value.validate()

  if (!validationResult.valid) {
    return
  }

  emit('submit', {
    brandCode: vehicleFormValues.brandCode,
    name: vehicleFormValues.name.trim(),
    plate: vehicleFormValues.plate.trim(),
    vin: vehicleFormValues.vin.trim(),
    color: vehicleFormValues.color.trim(),
    isForDrive: vehicleFormValues.usageArea === 'drivepark',
    isForEvent: vehicleFormValues.usageArea === 'events',
    isActive: vehicleFormValues.isActive,
    purpose: vehicleFormValues.purpose ?? '',
  })
}
</script>

<style
  scoped
  lang="scss"
>
.vehicle-form {
  &__usage-field {
    max-width: 560px;
  }

  &__usage-options {
    :deep(.v-selection-control-group) {
      gap: 24px;
    }
  }
}
</style>
